// Pure helpers for files in a HOSTED project database (task 2026-10-05 "files are uploaded into a
// live database"; backend operate/live_files.py, routers/data_files.py):
//   * the row form uploads a file first (POST .../tables/{table}/files?column=), then sends
//     {"<column>": {"upload": <object id>}} for it (`withUploads`);
//   * the table view shows the file a `<column>_object_id` cell names (`fileOf`, `fileSize`);
//   * an import lists the files delivered beside its table file (`filesSummary`).

/** The upload path of a file column (api.upload appends &filename=). */
export function uploadPath(projectId, table, column) {
  return `/projects/${projectId}/data/tables/${encodeURIComponent(table)}/files?column=${encodeURIComponent(column)}`;
}

/** The path of an import's files (api.upload appends ?filename=). */
export function importFilesPath(projectId, table, importId) {
  return `/projects/${projectId}/data/tables/${encodeURIComponent(table)}/imports/${importId}/files`;
}

/** `values` with each uploaded file sent as {upload: id} — when the form still holds the
 *  uploaded file's name for that column (a name typed over it is sent as typed). */
export function withUploads(values, formData, uploads) {
  const out = { ...values };
  Object.entries(uploads || {}).forEach(([column, up]) => {
    if (up && formData && formData[column] === up.filename) out[column] = { upload: up.object_id };
  });
  return out;
}

/** The file a cell names: a `<column>_object_id` value found in the page's `files`. */
export function fileOf(column, value, files) {
  if (value === null || value === undefined || !files || !column.endsWith("_object_id")) return null;
  return files[String(value)] || null;
}

/** 1536 -> "1.5 KB". */
export function fileSize(bytes) {
  if (bytes === null || bytes === undefined) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let n = Number(bytes);
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return i === 0 ? `${n} B` : `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

/** The API path of a download link (`files[id].download` starts with /api; api.download adds it). */
export function downloadPath(download) {
  return download && download.startsWith("/api/") ? download.slice(4) : download;
}

/** The preview's `files` (operate/imports.py) as sentences, in the order a person checks them. */
export function filesSummary(files) {
  if (!files) return [];
  const out = [`${files.delivered || 0} file(s) delivered, ${files.paired || 0} row(s) paired with a file.`];
  const row = x => `row ${x.row} (${x.value})`;
  if ((files.extension_mismatches || []).length) {
    out.push(`Wrong extension: ${files.extension_mismatches.map(row).join(", ")}.`);
  }
  if ((files.manifest_mismatches || []).length) {
    out.push(`Checksum differs from the manifest: ${files.manifest_mismatches.join(", ")}.`);
  }
  if ((files.rows_without_file || []).length) {
    out.push(`Rows without a file: ${files.rows_without_file.map(row).join(", ")}.`);
  }
  if ((files.files_without_row || []).length) {
    out.push(`Files no row names (not linked, removed later): ${files.files_without_row.join(", ")}.`);
  }
  if ((files.manifest_missing || []).length) {
    out.push(`In the manifest but not delivered: ${files.manifest_missing.join(", ")}.`);
  }
  return out;
}
